import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import Table from '../../../../base/components/Table';
import {
  usePaginationProvider,
  useSortProvider,
  useSearchProvider,
  useLocationSource,
  useFilterProvider
} from '../../../../base/components/Table/hooks';
import { useService } from "../../../../base/hooks/useService";
import { DEFAULT_TABLE_LIMIT } from '../../../../base/constants/shared';
import { LIMIT_OPTIONS } from '../../../../base/constants/pagination';
import { TableHeader, NoNotificationsPlaceholder, columns, SearchPlaceholder } from './components';
import NotificationsService from '../../../../services/NotificationsService';
import { DESC_SORT, NOTIFICATION_TYPES } from './constants';

export default function History() {
  const locationSource = useLocationSource();
  const notificationsService = useService(NotificationsService);
  const [notifications, setNotifications] = useState([]);
  const [pagination, setPagination] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const paginationProvider = usePaginationProvider({
    source: locationSource,
    alias: "offset",
    scope: "",
    fallback: 0
  });

  const limitProvider = usePaginationProvider({
    source: locationSource,
    alias: "limit",
    scope: "",
    fallback: DEFAULT_TABLE_LIMIT,
    onApplyClearScope: ["offset"]
  });

  const searchProvider = useSearchProvider({
    source: locationSource,
    scope: "",
    alias: 'search',
    onApplyClearScope: ["offset"]
  });

  const customerNameSortProvider = useSortProvider({
    source: locationSource,
    alias: 'customerName',
    scope: "",
    onApplyClearScope: ["offset"]
  });

  const customerIdSortProvider = useSortProvider({
    source: locationSource,
    alias: 'customerId',
    scope: "",
    onApplyClearScope: ["offset"]
  });

  const dateSortProvider = useSortProvider({
    source: locationSource,
    alias: 'date',
    scope: "",
    onApplyClearScope: ["offset"]
  });

  const typeSortProvider = useSortProvider({
    source: locationSource,
    alias: 'type',
    scope: "",
    onApplyClearScope: ["offset"]
  });

  const prevSortValues = useRef({
    customerName: null,
    customerId: null,
    date: null,
    type: null
  });

  useEffect(() => {
    const hasAnySort = customerNameSortProvider.getValue() || 
                      customerIdSortProvider.getValue() || 
                      dateSortProvider.getValue() || 
                      typeSortProvider.getValue();
    
    if (!hasAnySort) {
      dateSortProvider.setValue(DESC_SORT);
    }
  }, []);

  useEffect(() => {
    const customerNameSort = customerNameSortProvider.getValue();
    const customerIdSort = customerIdSortProvider.getValue();
    const dateSort = dateSortProvider.getValue();
    const typeSort = typeSortProvider.getValue();

    const currentSorts = {
      customerName: customerNameSort,
      customerId: customerIdSort,
      date: dateSort,
      type: typeSort
    };

    const hasChanged = Object.keys(currentSorts).some(
      key => currentSorts[key] !== prevSortValues.current[key]
    );

    if (hasChanged) {
      const newlyActivated = Object.keys(currentSorts).find(
        key => !prevSortValues.current[key] && currentSorts[key]
      );

      if (newlyActivated) {
        if (newlyActivated !== 'customerName' && customerNameSort) {
          customerNameSortProvider.setValue();
        }
        if (newlyActivated !== 'customerId' && customerIdSort) {
          customerIdSortProvider.setValue();
        }
        if (newlyActivated !== 'date' && dateSort) {
          dateSortProvider.setValue();
        }
        if (newlyActivated !== 'type' && typeSort) {
          typeSortProvider.setValue();
        }
      }

      prevSortValues.current = { ...currentSorts };
    }
  }, [
    customerNameSortProvider.getValue(),
    customerIdSortProvider.getValue(), 
    dateSortProvider.getValue(),
    typeSortProvider.getValue()
  ]);

  const typeFilterProvider = useFilterProvider({
    source: locationSource,
    scope: "",
    alias: 'typeFilter',
    onApplyClearScope: ["offset"]
  });

  const dateRangeFilterProvider = useFilterProvider({
    source: locationSource,
    scope: "",
    alias: 'dateRangeFilter',
    onApplyClearScope: ["offset"]
  });

  const filterProvider = useMemo(() => ({
    type: typeFilterProvider,
    dateRange: dateRangeFilterProvider
  }), [typeFilterProvider, dateRangeFilterProvider]);

  const notificationTypes = useMemo(() => {
    return NOTIFICATION_TYPES.map(type => ({
      value: type.toLowerCase(),
      label: type
    }));
  }, []);

  const offset = paginationProvider.getValue() || 0;
  const limit = limitProvider.getValue() || DEFAULT_TABLE_LIMIT;
  const search = searchProvider.getValue() || undefined;
  const typeString = typeFilterProvider.getValue() || undefined;
  const type = useMemo(() => {
    if (typeof typeString === 'string') {
      return typeString.split(',').map(t => t.trim()).filter(Boolean);
    }
    return typeString;
  }, [typeString]);
  const dateRange = dateRangeFilterProvider.getValue() || undefined;
  const customerNameSort = customerNameSortProvider.getValue();
  const customerIdSort = customerIdSortProvider.getValue();
  const dateSort = dateSortProvider.getValue();
  const typeSort = typeSortProvider.getValue();

  const [sortBy, sortOrder] = [
    customerNameSort ? ['user.firstName', customerNameSort] :
    customerIdSort ? ['userId', customerIdSort] :
    dateSort ? ['date', dateSort] :
    typeSort ? ['type', typeSort] :
    []
  ].flat();

  const fetchNotifications = useCallback(() => {
    setIsLoading(true);
    
    let fromDate, toDate;
    if (dateRange && Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
      fromDate = dateRange[0];
      toDate = dateRange[1];
    }
    
    const params = {
      offset,
      limit,
      search,
      from: fromDate,
      to: toDate,
      sortBy,
      sortOrder: sortOrder?.toUpperCase()
    };

    if (type && Array.isArray(type) && type.length > 0) {
      params.types = type;
    }

    Object.keys(params).forEach(key => {
      if (
        params[key] === undefined ||
        params[key] === null ||
        (Array.isArray(params[key]) && (
          params[key].length === 0 || 
          params[key].every(item => item === null || item === undefined)
        ))
      ) {
        delete params[key];
      }
    });

    notificationsService.getNotificationLogs(params)
      .then(response => {
        setNotifications(
          (response.logs || []).map(n => ({
            ...n,
            customerName: n.userName,
            customerId: n.userId
          }))
        );
        setPagination({
          totalCount: response.total || 0,
        });
      })
      .catch(() => {
        setNotifications([]);
        setPagination({ totalCount: 0 });
      })
      .finally(() => setIsLoading(false));
  }, [offset, limit, search, typeString, dateRange, sortBy, sortOrder, notificationsService]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <Table
      columns={notifications.length ? columns : []}
      data={notifications}
      loading={isLoading}
      HeaderComponent={TableHeader}
      totalCount={pagination.totalCount}
      limit={limitProvider.getValue()}
      offset={paginationProvider.getValue()}
      paginationProvider={paginationProvider}
      searchProvider={searchProvider}
      filterProvider={filterProvider}
      hasActiveFilters={!!typeFilterProvider.getValue() || !!dateRangeFilterProvider.getValue()}
      commonPlaceholder={<NoNotificationsPlaceholder />}
      placeholderForSearch={<SearchPlaceholder />}
      sortProviders={{
        customerName: customerNameSortProvider,
        customerId: customerIdSortProvider,
        date: dateSortProvider,
        type: typeSortProvider,
      }}
      isLimitEditable
      limitProvider={limitProvider}
      limitOptions={LIMIT_OPTIONS}
      notificationTypes={notificationTypes}
    />
  );
}